import { useState, useMemo, useCallback } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import { get, map, filter } from 'lodash'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CloudIcon from '@mui/icons-material/Cloud'
import DeleteIcon from '@mui/icons-material/Delete'
import { useConfirmationDialog } from '../../../system/ConfirmationDialogProvider'
import { useTeam } from '../../../contexts/TeamContext'
import { useHetznerProjects } from '../../../contexts/HetznerProjectsContext'
import { useHetznerServers } from '../../../contexts/HetznerServersContext'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import PageBody from '../../../shared/components/layouts/PageBody'
import Page from '../../../shared/components/layouts/Page'
import ResourceList from '../../../shared/components/layouts/ResourceList'
import ResourceListRow from '../../../shared/components/layouts/ResourceListRow'
import ResourceListCell from '../../../shared/components/layouts/ResourceListCell'
import Flex from '../../../shared/components/layouts/Flex'
import FlexColumn from '../../../shared/components/layouts/FlexColumn'
import HetznerServerStatusChip from './components/HetznerServerStatusChip'

function HetznerIndexPage() {
  const { teamSlug } = useParams()
  const intl = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { openConfirmationDialog } = useConfirmationDialog()
  const { team } = useTeam()
  const { projects, loading: projectsLoading, error: projectsError } = useHetznerProjects()
  const { servers, loading: serversLoading, error: serversError, deleteServer } = useHetznerServers()

  const [selectedProjectId, setSelectedProjectId] = useState(null)

  const handleDeleteServer = useCallback((server) => {
    openConfirmationDialog({
      title: intl.formatMessage({ id: 'hetzner.server.delete.title' }),
      message: intl.formatMessage({ id: 'hetzner.server.delete.message' }),
      onConfirm: () => deleteServer(server)
        .catch(() => {
          enqueueSnackbar(intl.formatMessage({ id: 'hetzner.server.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, intl, deleteServer, enqueueSnackbar])

  const loading = projectsLoading || serversLoading
  const error = projectsError || serversError

  const filteredServers = useMemo(() => {
    if (!selectedProjectId) return servers
    return filter(servers, (server) => server.project?.id === selectedProjectId)
  }, [servers, selectedProjectId])

  const serverCountByProjectId = useMemo(() => {
    const counts = {}
    servers.forEach((server) => {
      const projectId = server.project?.id
      counts[projectId] = (counts[projectId] || 0) + 1
    })
    return counts
  }, [servers])

  const addServerPath = useMemo(() => {
    if (selectedProjectId) {
      return `/teams/${teamSlug}/cloud/hetzner/servers/new?projectId=${selectedProjectId}`
    }
    return `/teams/${teamSlug}/cloud/hetzner/servers/new`
  }, [teamSlug, selectedProjectId])

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="hetzner.index.empty" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="hetzner.index.title" />}
        entities={{ team }}
        actions={
          <Flex gap={1}>
            <Button
              component={RouterLink}
              to={`/teams/${teamSlug}/cloud/hetzner/projects/new`}
              variant="outlined"
            >
              <FormattedMessage id="hetzner.index.addProject" />
            </Button>
            {projects.length > 0 && (
              <Button
                component={RouterLink}
                to={addServerPath}
                variant="contained"
                color="primary"
              >
                <FormattedMessage id="hetzner.index.addServer" />
              </Button>
            )}
          </Flex>
        }
      />
      <PageBody>
        {projects.length === 0 ? (
          <Typography color="text.secondary">
            <FormattedMessage id="hetzner.index.emptyProjects" />
          </Typography>
        ) : (
          <>
            <Flex gap={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={<FormattedMessage id="hetzner.index.filters.all" />}
                variant={!selectedProjectId ? 'filled' : 'outlined'}
                onClick={() => setSelectedProjectId(null)}
                clickable
              />
              {map(projects, (project) => {
                const serverCount = get(serverCountByProjectId, project.id, 0)
                return (
                  <Chip
                    key={project.id}
                    icon={<CloudIcon />}
                    label={
                      <Flex gap={1} sx={{ alignItems: 'center' }}>
                        {project.name}
                        {serverCount > 0 && (
                          <Typography
                            component="span"
                            sx={{
                              bgcolor: 'action.selected',
                              borderRadius: '8px',
                              minWidth: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              px: 0.5,
                            }}
                          >
                            {serverCount}
                          </Typography>
                        )}
                      </Flex>
                    }
                    variant={selectedProjectId === project.id ? 'filled' : 'outlined'}
                    color="secondary"
                    onClick={() => setSelectedProjectId(project.id)}
                    clickable
                  />
                )
              })}
            </Flex>

            <ResourceList
              items={filteredServers}
              renderItem={(server) => {
                const project = server.project

                return (
                  <ResourceListRow key={server.id}>
                    <ResourceListCell grow>
                      <FlexColumn gap={0.5}>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                          {server.name}
                        </Typography>
                        <Flex gap={2}>
                          <Typography variant="body2" color="text.secondary">
                            {server.serverType}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {server.datacenter}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {server.image}
                          </Typography>
                        </Flex>
                        {!selectedProjectId && project && (
                          <Chip
                            icon={<CloudIcon />}
                            label={project.name}
                            size="small"
                            color="secondary"
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}
                      </FlexColumn>
                    </ResourceListCell>

                    <ResourceListCell>
                      {server.publicIp && (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {server.publicIp}
                        </Typography>
                      )}
                    </ResourceListCell>

                    <ResourceListCell shrink={false}>
                      <HetznerServerStatusChip status={server.status} />
                    </ResourceListCell>

                    <ResourceListCell stopPropagation shrink>
                      <Tooltip title={<FormattedMessage id="hetzner.server.delete.button" />}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteServer(server)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ResourceListCell>
                  </ResourceListRow>
                )
              }}
              emptyMessageId="hetzner.index.empty"
            />
          </>
        )}
      </PageBody>
    </Page>
  )
}

export default HetznerIndexPage
